import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavBarLateral } from "./NavBarLateral";
import MiTabla from "../../_helpers/MiTabla";
import demandaService from "../../_services/cart/demanda.service";
import alertService from "../../_services/cart/alert.service";
import { productoActions } from "../../_actions/producto.actions";

/**
 * Qué está buscando la gente y qué de eso nadie publicó.
 *
 * <h3>La pantalla está ordenada por lo que se hace con el dato, no por lo que se midió</h3>
 * Arriba va "pedidos sin publicar", que es el único número sobre el que un vendedor puede actuar hoy
 * mismo. Los gráficos por marca y por tipo vienen después: sirven para entender el terreno, pero no
 * dicen qué hacer —cien pedidos de una marca no significan nada si ya hay doscientas publicaciones
 * de esa marca—. Y el detalle, que es la lista concreta de qué publicar, cierra abajo.
 *
 * <h3>Barras horizontales, no verticales</h3>
 * Las etiquetas son nombres de marca y de repuesto ("Amortiguadores delanteros"), que en vertical
 * hay que rotar o recortar. En horizontal el nombre se lee entero incluso en un teléfono, y la barra
 * crece hacia donde sobra espacio. Es la razón por la que los dos gráficos categóricos usan
 * `layout="vertical"` aunque se los llame "barras".
 */

/**
 * Colores de los gráficos.
 *
 * <p>Valores fijos y no variables del tema: el sitio invierte la escala de grises entre claro y
 * oscuro, y una serie categórica que cambia de color al cambiar el tema deja de ser comparable con
 * lo que la persona vio hace un minuto. Todos rondan el 50% de luminosidad, que es lo que los hace
 * legibles tanto sobre blanco como sobre el gris oscuro del tema dark.
 */
const PALETA = [
    "hsl(18, 100%, 50%)",
    "hsl(216, 75%, 55%)",
    "hsl(174, 62%, 42%)",
    "hsl(266, 60%, 60%)",
    "hsl(142, 55%, 45%)",
    "hsl(330, 70%, 60%)",
    "hsl(42, 95%, 52%)",
    "hsl(196, 80%, 48%)",
];

const VENTANAS = [
    { meses: 6, label: "6 meses" },
    { meses: 12, label: "12 meses" },
    { meses: 24, label: "24 meses" },
];

/** "2026-09" → "sep 26". El eje tiene que entrar en el ancho de un teléfono. */
const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const rotularPeriodo = (periodo) => {
    if (!periodo || periodo.length < 7) return periodo || "";
    const anio = periodo.slice(2, 4);
    const mes = parseInt(periodo.slice(5, 7), 10);
    return `${MESES_CORTOS[mes - 1] || periodo.slice(5, 7)} ${anio}`;
};

/** Recorta nombres largos para el eje sin romper el tooltip, que muestra el nombre completo. */
const acortar = (texto, max) => {
    if (!texto) return "";
    return texto.length > max ? `${texto.slice(0, max - 1)}…` : texto;
};

const Tile = ({ valor, etiqueta, detalle, destacado }) => (
    <div className={destacado ? "demanda__tile demanda__tile--destacado" : "demanda__tile"}>
        <span className="demanda__tile-valor">{valor}</span>
        <span className="demanda__tile-etiqueta">{etiqueta}</span>
        {detalle && <span className="demanda__tile-detalle">{detalle}</span>}
    </div>
);

/**
 * El tooltip de los gráficos.
 *
 * <p>Propio y no el de recharts porque el de fábrica hereda el fondo del contenedor: sobre el tema
 * oscuro queda texto gris sobre gris. Este pinta sus colores con las variables del sitio, que ya
 * están definidas para los dos temas.
 */
const TooltipDemanda = ({ active, payload, label, sufijo }) => {
    if (!active || !payload || payload.length === 0) return null;

    const fila = payload[0].payload;

    return (
        <div className="demanda__tooltip">
            <strong>{fila.nombreCompleto || label}</strong>
            {payload.map((serie) => (
                <span key={serie.dataKey}>
                    {serie.value} {sufijo}
                </span>
            ))}
        </div>
    );
};

/** La clave con la que se identifica una fila del panel: los cuatro ids que la definen. */
const claveFila = (fila) =>
    `${fila.marcaId}/${fila.modeloId}/${fila.versionId ?? ""}/${fila.tipoId ?? ""}`;

/** El texto con el que arranca la conversación, ya armado desde la fila. */
const mensajeWhatsapp = (fila) => {
    const auto = [fila.marca, fila.modelo, fila.version].filter(Boolean).join(" ");
    const repuesto = fila.tipoId ? fila.tipo : "el repuesto";

    return encodeURIComponent(
        `Hola! Te escribo de deautopartes.ar. Vi que estás buscando ${repuesto} para tu ${auto} ` +
            `y puedo conseguirlo. ¿Seguís necesitándolo?`
    );
};

const linkWhatsapp = (contacto, fila) =>
    `https://api.whatsapp.com/send?phone=${productoActions.normalizarTelefono(contacto.celular)}` +
    `&text=${mensajeWhatsapp(fila)}`;

/**
 * Los compradores que están esperando una fila, con el botón para escribirles.
 *
 * <p>Los contactos NO vienen en el panel: son cientos de filas y traerlos todos al entrar sería
 * bajar la agenda entera de compradores para dibujar cuatro gráficos. Se piden al abrir la fila, y
 * `cache` —un Map que vive en un ref de la pantalla— evita repetir el viaje si la vuelven a abrir.
 *
 * <p>Es un componente y no un render inline porque MiTabla llama a `detailPanel(row)` en cada
 * repintado: con la consulta acá adentro, montarse es lo que la dispara, y cerrar y abrir la fila
 * no vuelve a pegarle al server.
 */
const ContactosFila = ({ fila, cache }) => {
    const clave = claveFila(fila);

    const [contactos, setContactos] = useState(() => cache.get(clave));
    const [error, setError] = useState(null);

    useEffect(() => {
        if (cache.has(clave)) {
            setContactos(cache.get(clave));
            return undefined;
        }

        let cancelado = false;

        demandaService
            .contactos(fila)
            .then((data) => {
                cache.set(clave, data);
                if (!cancelado) setContactos(data);
            })
            .catch((e) => {
                if (!cancelado) setError(e?.message || "No se pudieron cargar los contactos.");
            });

        return () => {
            cancelado = true;
        };
        // `fila` se rearma en cada render del padre; la identidad de la consulta es la clave.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clave]);

    if (error) return <p className="demanda__contactos-estado">{error}</p>;
    if (!contactos) return <p className="demanda__contactos-estado">Cargando contactos…</p>;
    if (contactos.length === 0)
        return <p className="demanda__contactos-estado">No hay contactos para este pedido.</p>;

    return (
        <ul className="demanda__contactos">
            {contactos.map((c) => (
                <li key={c.userId} className="demanda__contacto">
                    <span className="demanda__contacto-nombre">
                        <strong>{c.nombre}</strong>
                        <small>
                            {c.desde ? `Espera desde ${rotularPeriodo(c.desde)}` : "Sin fecha"}
                            {c.alertas > 1 ? ` · ${c.alertas} alertas` : ""}
                        </small>
                    </span>

                    {c.celular ? (
                        <a
                            className="demanda__contacto-wp"
                            href={linkWhatsapp(c, fila)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon="fa-brands fa-whatsapp" size="lg" />
                            Escribirle
                        </a>
                    ) : (
                        <span className="demanda__contacto-sin-wp">Sin celular cargado</span>
                    )}
                </li>
            ))}
        </ul>
    );
};

const Demanda = () => {
    const [panel, setPanel] = useState(undefined);
    const [cargando, setCargando] = useState(true);
    const [meses, setMeses] = useState(12);
    const [soloSinCubrir, setSoloSinCubrir] = useState(false);
    const [busqueda, setBusqueda] = useState("");

    useEffect(() => {
        let cancelado = false;
        setCargando(true);

        demandaService
            .panel(meses, 100)
            .then((data) => {
                if (!cancelado) setPanel(data);
            })
            .catch((error) => {
                if (!cancelado) alertService.error(error);
            })
            .finally(() => {
                if (!cancelado) setCargando(false);
            });

        return () => {
            cancelado = true;
        };
    }, [meses]);

    const resumen = panel?.resumen;

    // Los gráficos categóricos comparten forma: nombre completo para el tooltip y uno recortado
    // para el eje. Se arma acá y no en el render para no recalcularlo en cada repintado del chart.
    const prepararConteos = useCallback(
        (lista) =>
            (lista || []).map((it) => ({
                ...it,
                nombreCompleto: it.nombre,
                nombre: acortar(it.nombre, 18),
            })),
        []
    );

    const marcas = useMemo(() => prepararConteos(panel?.porMarca), [panel, prepararConteos]);
    const tipos = useMemo(() => prepararConteos(panel?.porTipo), [panel, prepararConteos]);

    const serie = useMemo(
        () => (panel?.porMes || []).map((m) => ({ ...m, etiqueta: rotularPeriodo(m.periodo) })),
        [panel]
    );

    // El filtrado se hace acá y no dentro de MiTabla porque MiTabla sólo busca del lado del server
    // cuando `data` es una función; con un array plano su buscador no haría nada.
    const filas = useMemo(() => {
        const todas = panel?.demanda || [];
        const texto = busqueda.trim().toLowerCase();

        return todas.filter((f) => {
            if (soloSinCubrir && f.publicaciones > 0) return false;
            if (!texto) return true;

            return [f.marca, f.modelo, f.version, f.tipo]
                .filter(Boolean)
                .some((campo) => campo.toLowerCase().includes(texto));
        });
    }, [panel, soloSinCubrir, busqueda]);

    // MiTabla lee `data` una sola vez por consulta y su efecto no depende del prop: pasarle un array
    // nuevo no le mueve nada. `onQueryChange()` es el gancho que expone justo para esto —es lo que
    // usan las pantallas que buscan contra el server—, asi que el filtro de acá lo dispara igual.
    const tablaRef = useRef(null);

    // Los contactos ya pedidos, por fila. Es un ref y no estado: cambiarlo no tiene que repintar
    // nada —quien lo lee es el panel que se está montando—, y como estado forzaría un render de
    // toda la tabla cada vez que se abre una fila.
    const cacheContactos = useRef(new Map());

    // Si cambia la ventana de meses el panel se recarga entero y las filas pueden ser otras.
    useEffect(() => {
        cacheContactos.current.clear();
    }, [meses]);

    useEffect(() => {
        if (tablaRef.current) tablaRef.current.onQueryChange();
    }, [filas]);

    const columnas = [
        {
            title: "Auto",
            mobileTitle: true,
            render: (row) => (
                <span className="demanda__auto">
                    <strong>
                        {row.marca} {row.modelo}
                    </strong>
                    {row.version && <small>{row.version}</small>}
                </span>
            ),
        },
        {
            title: "Repuesto",
            render: (row) => <span>{row.tipo}</span>,
        },
        {
            title: "Personas esperando",
            render: (row) => <span className="demanda__numero">{row.personas}</span>,
        },
        {
            title: "Alertas",
            render: (row) => <span className="demanda__numero">{row.alertas}</span>,
        },
        {
            title: "Publicado",
            render: (row) =>
                row.publicaciones > 0 ? (
                    <span className="demanda__estado demanda__estado--cubierto">
                        {row.publicaciones} {row.publicaciones === 1 ? "publicación" : "publicaciones"}
                    </span>
                ) : (
                    <span className="demanda__estado demanda__estado--vacante">
                        <FontAwesomeIcon icon="fa-solid fa-bolt" /> Nadie lo publica
                    </span>
                ),
        },
    ];

    const sinDatos = !cargando && resumen && resumen.alertasActivas === 0;

    return (
        <div className="dashboard">
            <NavBarLateral handleClose={() => {}} />

            <div className="dashboard__content">
                <div className="dashboard__content-header">
                    <div className="dashboard__content-title">
                        <h2>Qué están buscando</h2>
                    </div>
                </div>

                <p className="demanda__bajada">
                    Cada vez que alguien no encuentra un repuesto, deja una alerta para que le avisemos
                    cuando aparezca. Esto es todo lo que están esperando hoy, y qué de eso nadie publicó
                    todavía.
                </p>

                {cargando && <p className="demanda__estado-carga">Cargando…</p>}

                {sinDatos && (
                    <div className="demanda__vacio">
                        <FontAwesomeIcon icon="fa-regular fa-bell" size="2x" />
                        <p>Todavía no hay alertas configuradas.</p>
                        <span>
                            Cuando los compradores empiecen a pedir repuestos que no encuentran, van a
                            aparecer acá.
                        </span>
                    </div>
                )}

                {!cargando && resumen && !sinDatos && (
                    <>
                        <section className="demanda__tiles">
                            <Tile
                                valor={resumen.pedidosSinCubrir}
                                etiqueta="Pedidos sin publicar"
                                detalle="Nadie tiene publicado lo que piden"
                                destacado
                            />
                            <Tile valor={resumen.personas} etiqueta="Personas esperando" />
                            <Tile valor={resumen.alertasActivas} etiqueta="Alertas activas" />
                            <Tile
                                valor={resumen.pedidosDistintos}
                                etiqueta="Pedidos distintos"
                                detalle="Combinaciones de auto y repuesto"
                            />
                        </section>

                        <section className="demanda__graficos">
                            <article className="demanda__card">
                                <h3>Marcas más buscadas</h3>
                                <div className="demanda__chart">
                                    <ResponsiveContainer width="100%" height={Math.max(200, marcas.length * 34)}>
                                        <BarChart data={marcas} layout="vertical" margin={{ left: 4, right: 16 }}>
                                            <CartesianGrid horizontal={false} stroke="var(--neutral-200, #d2d2d2)" />
                                            <XAxis type="number" allowDecimals={false} stroke="var(--neutral-500, #808080)" fontSize={12} />
                                            <YAxis
                                                type="category"
                                                dataKey="nombre"
                                                width={96}
                                                stroke="var(--neutral-500, #808080)"
                                                fontSize={12}
                                            />
                                            <Tooltip
                                                cursor={{ fill: "var(--neutral-100, #f2f2f2)" }}
                                                content={<TooltipDemanda sufijo="alertas" />}
                                            />
                                            <Bar dataKey="alertas" radius={[0, 4, 4, 0]}>
                                                {marcas.map((m, i) => (
                                                    <Cell key={m.id ?? i} fill={PALETA[i % PALETA.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </article>

                            <article className="demanda__card">
                                <h3>Repuestos más pedidos</h3>
                                <div className="demanda__chart">
                                    <ResponsiveContainer width="100%" height={Math.max(200, tipos.length * 34)}>
                                        <BarChart data={tipos} layout="vertical" margin={{ left: 4, right: 16 }}>
                                            <CartesianGrid horizontal={false} stroke="var(--neutral-200, #d2d2d2)" />
                                            <XAxis type="number" allowDecimals={false} stroke="var(--neutral-500, #808080)" fontSize={12} />
                                            <YAxis
                                                type="category"
                                                dataKey="nombre"
                                                width={96}
                                                stroke="var(--neutral-500, #808080)"
                                                fontSize={12}
                                            />
                                            <Tooltip
                                                cursor={{ fill: "var(--neutral-100, #f2f2f2)" }}
                                                content={<TooltipDemanda sufijo="alertas" />}
                                            />
                                            <Bar dataKey="alertas" radius={[0, 4, 4, 0]}>
                                                {tipos.map((t, i) => (
                                                    <Cell key={t.id ?? `sin-tipo-${i}`} fill={PALETA[i % PALETA.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </article>

                            <article className="demanda__card demanda__card--ancha">
                                <header className="demanda__card-head">
                                    <h3>Cómo viene la demanda</h3>
                                    <div className="demanda__ventanas">
                                        {VENTANAS.map((v) => (
                                            <button
                                                key={v.meses}
                                                type="button"
                                                className={
                                                    v.meses === meses
                                                        ? "demanda__ventana demanda__ventana--activa"
                                                        : "demanda__ventana"
                                                }
                                                onClick={() => setMeses(v.meses)}
                                            >
                                                {v.label}
                                            </button>
                                        ))}
                                    </div>
                                </header>

                                <div className="demanda__chart">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={serie} margin={{ left: 4, right: 16, top: 8 }}>
                                            <defs>
                                                <linearGradient id="gradDemanda" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={PALETA[0]} stopOpacity={0.45} />
                                                    <stop offset="100%" stopColor={PALETA[0]} stopOpacity={0.02} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid vertical={false} stroke="var(--neutral-200, #d2d2d2)" />
                                            <XAxis
                                                dataKey="etiqueta"
                                                stroke="var(--neutral-500, #808080)"
                                                fontSize={12}
                                                interval="preserveStartEnd"
                                                minTickGap={16}
                                            />
                                            <YAxis allowDecimals={false} stroke="var(--neutral-500, #808080)" fontSize={12} width={32} />
                                            <Tooltip content={<TooltipDemanda sufijo="alertas nuevas" />} />
                                            <Area
                                                type="monotone"
                                                dataKey="alertas"
                                                stroke={PALETA[0]}
                                                strokeWidth={2}
                                                fill="url(#gradDemanda)"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </article>
                        </section>

                        <section className="demanda__card">
                            <header className="demanda__card-head">
                                <h3>El detalle</h3>

                                <div className="demanda__filtros">
                                    <label className="demanda__buscador">
                                        <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                                        <input
                                            type="search"
                                            value={busqueda}
                                            placeholder="Buscar marca, modelo o repuesto"
                                            onChange={(e) => setBusqueda(e.target.value)}
                                        />
                                    </label>

                                    <button
                                        type="button"
                                        className={
                                            soloSinCubrir
                                                ? "demanda__chip demanda__chip--activo"
                                                : "demanda__chip"
                                        }
                                        onClick={() => setSoloSinCubrir((v) => !v)}
                                    >
                                        Solo lo que nadie publica
                                    </button>
                                </div>
                            </header>

                            <p className="demanda__hint">
                                Abrí una fila para ver quién lo está esperando y escribirle por WhatsApp.
                            </p>

                            <MiTabla
                                title=""
                                tableRef={tablaRef}
                                columns={columnas}
                                data={filas}
                                detailPanel={(row) => (
                                    <ContactosFila fila={row} cache={cacheContactos.current} />
                                )}
                                options={{ search: false, sorting: false, paging: false }}
                                localization={{
                                    body: { emptyDataSourceMessage: "No hay pedidos que coincidan." },
                                }}
                            />
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export { Demanda };
export default Demanda;
